import {
  Landmark,
  ArrowUpCircle,
  ArrowDownCircle,
  PiggyBank,
  FileCheck2,
  Flame,
  Coins,
  Building2,
  Repeat,
  Vault,
  Boxes,
} from "lucide-react";

// Field types: text | number | money | date | select | combo
// Column types: text | money | date | badge | status | percent | rank

export const INCOME_CATEGORIES = ["Subscriptions", "Services", "Revenue", "Interest", "Grants", "Refund", "Other"];
export const ASSET_CATEGORIES = ["Fixed Assets", "Equipment", "ID Card", "Laptop", "Software License", "Furniture", "Vehicle", "Other"];
export const EXPENSE_CATEGORIES = [
  "Admin Payments", "Compliance Cost", "Hiring Cost", "Infra Payments",
  "Statutory Payments", "Tech/Cloud Payments", "Finance Cost",
  "Office Rental", "Expert Consultants", "Manpower", "Other",
];

export const ENTITY_CONFIG = {
  vendors: {
    apiPath: "vendors",
    title: "Vendors",
    subtitle: "Master list of vendors & suppliers — linked across Expenses and Vendors Payable",
    icon: Building2,
    accent: "info",
    rowDetail: true,
    activityRole: "vendor",
    fields: [
      { name: "name", label: "Vendor Name", type: "text", required: true },
      { name: "category", label: "Category", type: "combo", catType: "expense", options: EXPENSE_CATEGORIES },
      { name: "currency", label: "Default Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"] },
      { name: "tds_rate", label: "TDS Rate (%)", type: "number" },
      { name: "tds_section", label: "TDS Section", type: "text" },
      { name: "contact", label: "Contact (email / phone)", type: "text" },
      { name: "gstin", label: "GSTIN", type: "text" },
      { name: "pan", label: "PAN", type: "text" },
      { name: "tan", label: "TAN", type: "text" },
      { name: "address", label: "Address", type: "text" },
      { name: "description", label: "Notes", type: "text" },
    ],
    columns: [
      { key: "name", label: "Vendor", type: "text" },
      { key: "category", label: "Category", type: "badge" },
      { key: "tds_rate", label: "TDS%", type: "text" },
      { key: "contact", label: "Contact", type: "text" },
      { key: "gstin", label: "GSTIN", type: "text" },
      { key: "pan", label: "PAN", type: "text" },
      { key: "currency", label: "Ccy", type: "badge" },
    ],
  },

  debtors: {
    apiPath: "debtors",
    title: "Debtors",
    subtitle: "Master list of customers / debtors — linked across Income and ARR Receivables",
    icon: Building2,
    accent: "success",
    rowDetail: true,
    activityRole: "debtor",
    fields: [
      { name: "name", label: "Debtor / Customer Name", type: "text", required: true },
      { name: "category", label: "Category", type: "combo", catType: "income", options: INCOME_CATEGORIES },
      { name: "currency", label: "Default Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"] },
      { name: "contact", label: "Contact (email / phone)", type: "text" },
      { name: "gstin", label: "GSTIN", type: "text" },
      { name: "pan", label: "PAN", type: "text" },
      { name: "tan", label: "TAN", type: "text" },
      { name: "address", label: "Address", type: "text" },
      { name: "description", label: "Notes", type: "text" },
    ],
    columns: [
      { key: "name", label: "Debtor", type: "text" },
      { key: "category", label: "Category", type: "badge" },
      { key: "contact", label: "Contact", type: "text" },
      { key: "gstin", label: "GSTIN", type: "text" },
      { key: "pan", label: "PAN", type: "text" },
      { key: "currency", label: "Ccy", type: "badge" },
    ],
  },

  banks: {
    apiPath: "bank-accounts",
    title: "Bank Balances",
    subtitle: "Liquid cash across operating, settlement & reserve accounts",
    icon: Landmark,
    amountField: "balance",
    amountLabel: "Total Liquid Cash",
    accent: "info",
    fields: [
      { name: "name", label: "Account Name", type: "text", required: true },
      { name: "bank", label: "Bank", type: "text", required: true },
      { name: "account_number", label: "Bank Account Number", type: "text" },
      { name: "account_type", label: "Type", type: "select", options: ["Current", "Savings", "Settlement", "OD"] },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "balance", label: "Balance", type: "money", required: true },
    ],
    columns: [
      { key: "name", label: "Account", type: "text" },
      { key: "bank", label: "Bank", type: "text" },
      { key: "account_number", label: "A/C Number", type: "text" },
      { key: "account_type", label: "Type", type: "badge" },
      { key: "currency", label: "Ccy", type: "badge" },
      { key: "balance", label: "Balance", type: "money" },
    ],
  },

  income: {
    apiPath: "income",
    monthField: "date",
    accountField: "bank_account_id",
    title: "Income & Sources",
    subtitle: "Revenue inflows categorised by source — feeds MIS category breakdown",
    icon: Coins,
    amountField: "amount",
    amountLabel: "Total Income",
    accent: "success",
    sortDesc: true,
    statusField: "status",
    taggable: true,
    fields: [
      { name: "source", label: "Source / Client", type: "text", required: true },
      { name: "category", label: "Category", type: "select", catType: "income", options: INCOME_CATEGORIES, required: true },
      { name: "invoice_no", label: "Invoice No.", type: "text" },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "bank_account_id", label: "Bank Account", type: "account" },
      { name: "status", label: "Status", type: "select", options: ["Received", "Receivable"] },
      { name: "description", label: "Description", type: "text" },
      { name: "attachments", label: "Attachments (receipts / invoices)", type: "attachments" },
    ],
    columns: [
      { key: "date", label: "Date", type: "date" },
      { key: "status", label: "Status", type: "status" },
      { key: "source", label: "Source", type: "text" },
      { key: "category", label: "Category", type: "badge" },
      { key: "invoice_no", label: "Invoice", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "bank_account_id", label: "Account", type: "account" },
      { key: "attachments", label: "Files", type: "attachments" },
      { key: "currency", label: "Ccy", type: "badge" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "allocations", label: "Cost Tag", type: "alloc" },
      { key: "created_by", label: "Entered By", type: "user" },
    ],
  },

  sweeps: {
    apiPath: "sweeps",
    monthField: "date",
    accountField: "bank_account_id",
    title: "Sweep & FD",
    subtitle: "Sweep-in credits & fixed-deposit transfers — excluded from Income, Transfers & MIS",
    icon: Repeat,
    amountField: "amount",
    amountLabel: "Total Sweep Balance",
    accent: "info",
    sortDesc: true,
    fields: [
      { name: "counterparty", label: "Counterparty / FD Account", type: "text", required: true },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "direction", label: "Direction", type: "select", options: ["credit", "debit"] },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "bank_account_id", label: "Bank Account", type: "account" },
      { name: "description", label: "Description", type: "text" },
    ],
    columns: [
      { key: "date", label: "Date", type: "date" },
      { key: "counterparty", label: "Counterparty", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "bank_account_id", label: "Account", type: "account" },
      { key: "direction", label: "CR/DR", type: "direction" },
      { key: "currency", label: "Ccy", type: "badge" },
      { key: "amount", label: "Amount", type: "money" },
    ],
  },

  transfers: {
    apiPath: "transfers",
    monthField: "date",
    accountField: "bank_account_id",
    title: "Internal Transfers",
    subtitle: "Bank-to-bank / own-account movements — excluded from Income, Expenses & MIS",
    icon: ArrowUpCircle,
    amountField: "amount",
    amountLabel: "Total Transferred",
    accent: "info",
    sortDesc: true,
    fields: [
      { name: "counterparty", label: "Counterparty / Account", type: "text", required: true },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "direction", label: "Direction", type: "select", options: ["credit", "debit"] },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "bank_account_id", label: "Bank Account", type: "account" },
      { name: "description", label: "Description", type: "text" },
    ],
    columns: [
      { key: "date", label: "Date", type: "date" },
      { key: "counterparty", label: "Counterparty", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "bank_account_id", label: "Account", type: "account" },
      { key: "direction", label: "CR/DR", type: "direction" },
      { key: "currency", label: "Ccy", type: "badge" },
      { key: "amount", label: "Amount", type: "money" },
    ],
  },

  payables: {
    apiPath: "payables",
    monthField: "due_date",
    title: "Vendors Payable",
    subtitle: "Outstanding amounts owed to vendors & suppliers",
    icon: ArrowUpCircle,
    amountField: "amount",
    amountLabel: "Total Payable",
    accent: "danger",
    statusField: "status",
    rowDetail: true,
    detailTitleField: "vendor",
    aging: { dateField: "due_date" },
    fields: [
      { name: "vendor", label: "Vendor", type: "entity-ref", entity: "vendors", optionValue: "name", labelField: "category", autofill: { category: "category", currency: "currency" }, required: true },
      { name: "invoice_no", label: "Invoice No.", type: "text" },
      { name: "category", label: "Category", type: "combo", catType: "expense", options: EXPENSE_CATEGORIES },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "paid_amount", label: "Advance / Paid so far", type: "money", hint: "Advance already paid against this invoice. Deducted from Total Payable + aging." },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Pending", "Partial", "Paid", "Overdue"] },
      { name: "attachments", label: "Invoice / Attachments", type: "attachments" },
    ],
    columns: [
      { key: "vendor", label: "Vendor", type: "text" },
      { key: "invoice_no", label: "Invoice", type: "text" },
      { key: "category", label: "Category", type: "badge" },
      { key: "due_date", label: "Due", type: "date" },
      { key: "status", label: "Status", type: "status" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "created_by", label: "Entered By", type: "user" },
    ],
  },

  receivables: {
    apiPath: "receivables",
    monthField: "due_date",
    title: "ARR Receivable",
    subtitle: "Recurring & one-time revenue invoiced and pending collection",
    icon: ArrowDownCircle,
    amountField: "amount",
    amountLabel: "Total Receivable",
    accent: "success",
    statusField: "status",
    aging: { dateField: "due_date" },
    fields: [
      { name: "client", label: "Client", type: "text", required: true },
      { name: "invoice_no", label: "Invoice No.", type: "text" },
      { name: "type", label: "Type", type: "select", options: ["ARR", "One-time"] },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Pending", "Paid", "Overdue"] },
    ],
    columns: [
      { key: "client", label: "Client", type: "text" },
      { key: "invoice_no", label: "Invoice", type: "text" },
      { key: "type", label: "Type", type: "badge" },
      { key: "due_date", label: "Due", type: "date" },
      { key: "status", label: "Status", type: "status" },
      { key: "amount", label: "Amount", type: "money" },
    ],
  },

  investments: {
    apiPath: "investments",
    title: "Fixed Investments",
    subtitle: "Deposits, bonds, treasury & managed funds",
    icon: PiggyBank,
    amountField: "current_value",
    amountLabel: "Portfolio Value",
    accent: "primary",
    fields: [
      { name: "name", label: "Instrument", type: "text", required: true },
      { name: "type", label: "Type", type: "select", options: ["Fixed Deposit", "Mutual Fund", "Bonds", "T-Bills", "Equity"] },
      { name: "principal", label: "Principal", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "interest_rate", label: "Rate (%)", type: "number" },
      { name: "maturity_date", label: "Maturity", type: "date" },
      { name: "current_value", label: "Current Value", type: "money" },
    ],
    columns: [
      { key: "name", label: "Instrument", type: "text" },
      { key: "type", label: "Type", type: "badge" },
      { key: "principal", label: "Principal", type: "money" },
      { key: "interest_rate", label: "Rate", type: "percent" },
      { key: "maturity_date", label: "Maturity", type: "date" },
      { key: "current_value", label: "Value", type: "money" },
    ],
  },

  firc: {
    apiPath: "firc",
    monthField: "date_submitted",
    title: "FIRCs vs Invoices",
    subtitle: "Foreign Inward Remittance Certificates against export invoices",
    icon: FileCheck2,
    amountField: "firc_amount",
    amountLabel: "Total FIRC Value",
    accent: "info",
    statusField: "status",
    fields: [
      { name: "firc_no", label: "FIRC No.", type: "text", required: true },
      { name: "invoice_no", label: "Against Invoice", type: "entity-ref", entity: "receivables", optionValue: "invoice_no", labelField: "client", autofill: { invoice_amount: "amount", currency: "currency" }, required: true },
      { name: "invoice_amount", label: "Invoice Amount", type: "money" },
      { name: "firc_amount", label: "FIRC Amount", type: "money" },
      { name: "currency", label: "Currency", type: "select", options: ["USD", "EUR", "GBP", "INR"], required: true },
      { name: "bank", label: "Bank", type: "text" },
      { name: "date_submitted", label: "Submitted On", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Pending", "Submitted", "Received"] },
    ],
    columns: [
      { key: "firc_no", label: "FIRC No.", type: "text" },
      { key: "invoice_no", label: "Invoice", type: "text" },
      { key: "invoice_amount", label: "Invoice Amt", type: "money" },
      { key: "firc_amount", label: "FIRC Amt", type: "money" },
      { key: "bank", label: "Bank", type: "text" },
      { key: "date_submitted", label: "Submitted", type: "date" },
      { key: "status", label: "Status", type: "status" },
    ],
  },

  expenses: {
    apiPath: "expenses",
    monthField: "date",
    accountField: "bank_account_id",
    title: "Expenses",
    subtitle: "Cash outflows categorised — ranked by amount, feeds MIS breakdown",
    icon: Flame,
    amountField: "amount",
    amountLabel: "Total Expenses",
    accent: "danger",
    sortDesc: true,
    statusField: "status",
    rowDetail: true,
    detailTitleField: "vendor",
    taggable: true,
    fields: [
      { name: "category", label: "Category", type: "select", catType: "expense", options: EXPENSE_CATEGORIES, required: true },
      { name: "vendor", label: "Vendor / Payee", type: "entity-ref", entity: "vendors", optionValue: "name", labelField: "category", autofill: { category: "category", currency: "currency" } },
      { name: "invoice_no", label: "Invoice No. (auto-reconciles Payables)", type: "text" },
      { name: "amount", label: "Amount", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "bank_account_id", label: "Bank Account", type: "account" },
      { name: "status", label: "Status", type: "select", options: ["Paid", "Payable"] },
      { name: "description", label: "Description", type: "text" },
      { name: "attachments", label: "Attachments (receipts / invoices)", type: "attachments" },
    ],
    columns: [
      { key: "rank", label: "#", type: "rank" },
      { key: "date", label: "Date", type: "date" },
      { key: "status", label: "Status", type: "status" },
      { key: "category", label: "Category", type: "badge" },
      { key: "vendor", label: "Payee", type: "text" },
      { key: "invoice_no", label: "Invoice", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "bank_account_id", label: "Account", type: "account" },
      { key: "attachments", label: "Files", type: "attachments" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "allocations", label: "Cost Tag", type: "alloc" },
      { key: "created_by", label: "Entered By", type: "user" },
    ],
  },


  fixedAssets: {
    apiPath: "fixed-assets",
    monthField: "purchase_date",
    title: "Fixed Assets Register",
    subtitle: "Capitalised items — laptops, equipment, ID cards, furniture. Balance-sheet asset, not P&L expense.",
    icon: Boxes,
    amountField: "cost",
    amountLabel: "Total Asset Cost",
    accent: "info",
    statusField: "status",
    fields: [
      { name: "name", label: "Asset Name / Description", type: "text", required: true },
      { name: "narration", label: "Narration (purpose / nature)", type: "text" },
      { name: "category", label: "Category", type: "select", catType: "asset", options: ASSET_CATEGORIES, required: true },
      { name: "asset_tag", label: "Asset Tag / Serial", type: "text" },
      { name: "vendor", label: "Vendor / Supplier", type: "entity-ref", entity: "vendors", optionValue: "name" },
      { name: "invoice_no", label: "Invoice No.", type: "text" },
      { name: "purchase_date", label: "Purchase Date", type: "date", required: true },
      { name: "cost", label: "Purchase Cost", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"], required: true },
      { name: "assigned_to", label: "Assigned To", type: "text" },
      { name: "location", label: "Location", type: "text" },
      { name: "useful_life_years", label: "Useful Life (yrs)", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["Active", "In Repair", "Retired", "Written Off", "Sold"] },
      { name: "notes", label: "Notes", type: "text" },
    ],
    columns: [
      { key: "name", label: "Asset", type: "text" },
      { key: "narration", label: "Narration", type: "text" },
      { key: "category", label: "Category", type: "badge" },
      { key: "asset_tag", label: "Tag", type: "text" },
      { key: "assigned_to", label: "Assigned To", type: "text" },
      { key: "purchase_date", label: "Purchased", type: "date" },
      { key: "cost", label: "Cost", type: "money" },
      { key: "currency", label: "Ccy", type: "badge" },
      { key: "status", label: "Status", type: "status" },
    ],
  },

  fixedDeposits: {
    apiPath: "fixed-deposits",
    monthField: "start_date",
    title: "Fixed Deposits",
    subtitle: "FD portfolio — upload your bank FD summary and AI extracts every deposit",
    icon: Vault,
    amountField: "current_value",
    amountLabel: "Total FD Portfolio",
    accent: "primary",
    statusField: "status",
    searchPlaceholder: "Search FD number, bank, branch…",
    fields: [
      { name: "fd_number", label: "FD / Receipt No.", type: "text", required: true },
      { name: "bank", label: "Bank", type: "text", required: true },
      { name: "branch", label: "Branch", type: "text" },
      { name: "principal", label: "Principal", type: "money", required: true },
      { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP", "AED"], required: true },
      { name: "interest_rate", label: "Rate (% p.a.)", type: "number" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "maturity_date", label: "Maturity Date", type: "date" },
      { name: "tenure_days", label: "Tenure (days)", type: "number" },
      { name: "payout_type", label: "Interest Payout", type: "select", options: ["Cumulative", "Monthly", "Quarterly", "Half-Yearly", "Yearly", "On Maturity"] },
      { name: "current_value", label: "Maturity / Current Value", type: "money" },
      { name: "status", label: "Status", type: "select", options: ["Active", "Matured", "Closed", "Auto-Renewed", "Broken"] },
      { name: "linked_account", label: "Linked / Source Account", type: "text" },
    ],
    columns: [
      { key: "fd_number", label: "FD No.", type: "text" },
      { key: "bank", label: "Bank", type: "text" },
      { key: "principal", label: "Principal", type: "money" },
      { key: "interest_rate", label: "Rate", type: "percent" },
      { key: "start_date", label: "Start", type: "date" },
      { key: "maturity_date", label: "Maturity", type: "date" },
      { key: "payout_type", label: "Payout", type: "badge" },
      { key: "current_value", label: "Value", type: "money" },
      { key: "status", label: "Status", type: "status" },
    ],
  },
};
